import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { LoginForm } from '@/components/LoginForm';
import { Logo } from '@/components/Logo';

import { useQuery } from 'hooks/query';
import { authController } from '@/states/authStateController';

import { ChainlitContext, useAuth } from '@chainlit/react-client';

export const LoginError = new Error('Error logging in. Please try again later.');

/**
 * Helper function to wait for cookies to be available in Safari
 * Safari needs a small delay after setting cookies before they're accessible
 */
const waitForCookies = (ms: number = 300): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export default function Login() {
  const query = useQuery();
  const { data: config, user, setUserFromAPI } = useAuth();
  const [error, setError] = useState('');
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  const headerAuthAttempted = useRef(false);
  const isProcessingAuth = useRef(false);

  const handleCookieAuth = async (json: any): Promise<void> => {
    if (json?.success != true) throw LoginError;

    // In Safari, cookies may not be immediately available after being set
    // Wait a bit for cookies to propagate before trying to fetch user
    // Safari needs more time than other browsers
    await waitForCookies(500);

    // Validate login cookie and get user data.
    // Retry a few times in case cookies aren't ready yet (Safari issue)
    let retries = 5;
    let success = false;

    while (retries > 0 && !success) {
      setUserFromAPI();
      // Give it a moment to update state and check if user was set
      await waitForCookies(200);

      // Check if user was successfully set by checking the auth state
      // We'll rely on the user state being updated by setUserFromAPI
      // If it's still null after a few retries, we continue anyway
      // The useEffect will handle navigation once user is set
      retries--;

      if (retries === 0) {
        // Last attempt - wait a bit longer
        await waitForCookies(300);
        setUserFromAPI();
      }
    }
  };

  const handleAuth = async (jsonPromise: Promise<any>, redirectURL?: string) => {
    if (isProcessingAuth.current) {
      return; // Prevent multiple simultaneous auth attempts
    }

    try {
      isProcessingAuth.current = true;
      const json = await jsonPromise;

      await handleCookieAuth(json);

      // Wait a bit more before navigating to ensure state is updated
      await waitForCookies(100);

      if (redirectURL) {
        navigate(redirectURL);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      isProcessingAuth.current = false;
    }
  };

  const handleHeaderAuth = async () => {
    if (headerAuthAttempted.current || isProcessingAuth.current) {
      return; // Prevent multiple calls
    }

    headerAuthAttempted.current = true;
    const jsonPromise = apiClient.headerAuth();

    // Why does apiClient redirect to '/' but handlePasswordLogin to callbackUrl?
    handleAuth(jsonPromise, '/').catch(() => {
      // Reset on error so user can retry
      headerAuthAttempted.current = false;
    });
  };

  const handlePasswordLogin = async (email: string, password: string) => {
    if (isProcessingAuth.current) {
      return; // Prevent multiple simultaneous login attempts
    }

    try {
      isProcessingAuth.current = true;
      // First authenticate with Chainlit
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      authController.updateState({ isLoading: true, user: null });

      try {
        const chainlitResponse = await apiClient.passwordAuth(formData);
        // Wait for cookies to be set and available (important for Safari)
        await handleCookieAuth(chainlitResponse);
      } catch (chainlitError) {
        console.warn('Chainlit authentication failed:', chainlitError);
        setError('Authentication failed. Please check your credentials.');
        isProcessingAuth.current = false;
        return;
      }

      // Then get payment status from our API
      const result = await authController.login(email, password);

      if (result.success) {
        // Require active subscription AND a valid, future expiry date
        const hasActiveSubscription = !!result.user?.is_subscribed;
        const hasValidExpiryDate = result.user?.subscription_expiry_date
          ? new Date(result.user.subscription_expiry_date) > new Date()
          : false;
        const isActive = hasActiveSubscription || hasValidExpiryDate;

        // Wait a bit more to ensure all state is updated before navigating
        await waitForCookies(100);

        if (!isActive) {
          navigate('/payment');
        } else {
          navigate('/');
        }
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (error: any) {
      setError(error.message || 'Network error occurred');
    } finally {
      isProcessingAuth.current = false;
    }
  };

  useEffect(() => {
    setError(query.get('error') || '');
  }, [query]);

  useEffect(() => {
    if (!config) {
      return;
    }

    // If login is not required, redirect to home
    if (!config.requireLogin) {
      navigate('/');
      return;
    }

    // If user is already authenticated, redirect to home
    // Only navigate if we're actually on the login page to prevent loops
    if (user && window.location.pathname.includes('/login')) {
      navigate('/', { replace: true });
      return;
    }

    // Only handle header auth if configured and user is not set
    // Use a ref to prevent multiple calls (important for Safari)
    if (
      config.headerAuth &&
      !user &&
      !headerAuthAttempted.current &&
      !isProcessingAuth.current
    ) {
      handleHeaderAuth();
    }
  }, [config, user, navigate]);

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <Logo className="w-[150px]" />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            <LoginForm
              error={error}
              callbackUrl="/"
              providers={config?.oauthProviders || []}
              onPasswordSignIn={config?.passwordAuth ? handlePasswordLogin : undefined}
              onOAuthSignIn={async (provider: string) => {
                window.location.href = apiClient.getOAuthEndpoint(provider);
              }}
            />
          </div>
        </div>
      </div>
      {!config?.headerAuth ? (
        <div className="relative hidden bg-muted lg:block">
          <img
            src={apiClient.buildEndpoint('/favicon')}
            alt="Image"
            className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
          />
        </div>
      ) : null}
    </div>
  );
}
